document.documentElement.classList.add('b1g2-enhanced');

const B1G2_PAGE_VERSION = '2026-08-20-ga4-routing';
const B1G2_GA4_MEASUREMENT_ID = 'G-V2XWEDE2E3';
const B1G2_RENDER_CHUNK_SIZE = 8;
const B1G2_RENDER_ROOT_MARGIN = '600px 0px';
const b1g2EventTimings = [];
const b1g2LongTasks = [];
let b1g2Ga4Configured = false;

const roundB1g2Timing = (value) => Math.round(Math.max(0, value) * 10) / 10;

const getB1g2TimingBucket = (duration) => {
  if (duration < 100) return 'under-100';
  if (duration < 200) return '100-199';
  if (duration < 500) return '200-499';
  return '500-plus';
};

const getB1g2EventStart = (event, fallback) => {
  let startTime = Number(event?.timeStamp);
  if (!Number.isFinite(startTime)) return fallback;
  if (startTime > performance.timeOrigin) startTime -= performance.timeOrigin;
  return Math.min(startTime, fallback);
};

const observeB1g2Performance = (type, buffer) => {
  if (typeof PerformanceObserver !== 'function') return;
  if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      buffer.push(...list.getEntries());
      if (buffer.length > 100) buffer.splice(0, buffer.length - 100);
    });
    const options = { type, buffered: true };
    if (type === 'event') options.durationThreshold = 16;
    observer.observe(options);
  } catch (error) {
    // The custom timing fallback below still works when an observer type is unavailable.
  }
};

observeB1g2Performance('event', b1g2EventTimings);
observeB1g2Performance('longtask', b1g2LongTasks);

const trackB1g2Event = (eventName, details = {}) => {
  const trackedEvent = `b1g2_${eventName}`;
  const payload = {
    event: trackedEvent,
    b1g2_page_version: B1G2_PAGE_VERSION,
    b1g2_page_path: window.location.pathname,
    ...details,
  };

  if (typeof window.gtag === 'function') {
    const { event, ...parameters } = payload;
    if (!b1g2Ga4Configured) {
      window.gtag('config', B1G2_GA4_MEASUREMENT_ID, { send_page_view: false });
      b1g2Ga4Configured = true;
    }
    window.gtag('event', event, {
      ...parameters,
      send_to: B1G2_GA4_MEASUREMENT_ID,
    });
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ...payload,
      send_to: B1G2_GA4_MEASUREMENT_ID,
    });
  }

  if (typeof window.clarity === 'function') {
    window.clarity('set', 'b1g2_page_version', B1G2_PAGE_VERSION);
    window.clarity('set', 'b1g2_event', trackedEvent);
    window.clarity('event', trackedEvent);
  }

  document.dispatchEvent(new CustomEvent(trackedEvent, { detail: payload }));
};

const trackB1g2Performance = (eventName, details) => {
  if (typeof window.clarity === 'function') {
    window.clarity('set', 'b1g2_perf_source', details.b1g2_perf_source);
    window.clarity('set', 'b1g2_perf_total_bucket', getB1g2TimingBucket(details.b1g2_total_ms));
    window.clarity('set', 'b1g2_perf_slowest_phase', details.b1g2_slowest_phase || 'unknown');
    window.clarity('set', 'b1g2_perf_long_task', details.b1g2_long_task_count ? 'yes' : 'no');
  }
  trackB1g2Event(eventName, details);
};

const beginB1g2Interaction = (event, source) => {
  const handlerStart = performance.now();
  const eventStart = getB1g2EventStart(event, handlerStart);

  return (details = {}) => {
    const processingEnd = performance.now();
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const measuredAt = performance.now();
        const eventTiming = b1g2EventTimings
          .filter((entry) => entry.startTime >= eventStart - 50 && entry.startTime <= processingEnd + 50)
          .sort((a, b) => b.duration - a.duration)[0];
        const timingStart = eventTiming?.startTime ?? eventStart;
        const timingEnd = eventTiming ? eventTiming.startTime + eventTiming.duration : measuredAt;
        const relatedLongTasks = b1g2LongTasks.filter(
          (entry) => entry.startTime < timingEnd && entry.startTime + entry.duration > timingStart
        );
        const phaseTimings = {
          option: details.b1g2_option_ms,
          counts: details.b1g2_counts_ms,
          filter_dom: details.b1g2_filter_dom_ms,
          tabs: details.b1g2_tabs_ms,
          url: details.b1g2_url_ms,
          tracking: details.b1g2_tracking_ms,
        };
        const slowestPhase = Object.entries(phaseTimings)
          .filter(([, duration]) => Number.isFinite(duration))
          .sort(([, a], [, b]) => b - a)[0]?.[0] || 'other';

        trackB1g2Performance('interaction_perf', {
          ...details,
          b1g2_perf_source: source,
          b1g2_perf_event: event?.type || 'unknown',
          b1g2_timing_api: eventTiming ? 'event-timing' : 'fallback',
          b1g2_input_delay_ms: roundB1g2Timing(
            eventTiming ? eventTiming.processingStart - eventTiming.startTime : handlerStart - eventStart
          ),
          b1g2_processing_ms: roundB1g2Timing(
            eventTiming ? eventTiming.processingEnd - eventTiming.processingStart : processingEnd - handlerStart
          ),
          b1g2_presentation_ms: roundB1g2Timing(
            eventTiming
              ? eventTiming.duration - (eventTiming.processingEnd - eventTiming.startTime)
              : measuredAt - processingEnd
          ),
          b1g2_total_ms: roundB1g2Timing(eventTiming?.duration ?? measuredAt - eventStart),
          b1g2_interaction_id: eventTiming?.interactionId || 0,
          b1g2_long_task_count: relatedLongTasks.length,
          b1g2_long_task_total_ms: roundB1g2Timing(
            relatedLongTasks.reduce((total, entry) => total + entry.duration, 0)
          ),
          b1g2_long_task_max_ms: roundB1g2Timing(
            Math.max(0, ...relatedLongTasks.map((entry) => entry.duration))
          ),
          b1g2_slowest_phase: slowestPhase,
        });
      }, 0);
    });
  };
};

const matchesB1g2Offer = (filterRole, productRole) => {
  if (filterRole === 'all') return true;
  if (filterRole === 'b1') return productRole === 'b1' || productRole === 'b1g2';
  if (filterRole === 'g2') return productRole === 'g2' || productRole === 'b1g2';
  return productRole === filterRole;
};

const matchesB1g2Price = (filterPrice, productPrice) => {
  if (filterPrice === 'all') return true;
  if (filterPrice === 'under-1000') return productPrice < 100000;
  if (filterPrice === '1000-1999') return productPrice >= 100000 && productPrice < 200000;
  if (filterPrice === '2000-2499') return productPrice >= 200000 && productPrice < 250000;
  if (filterPrice === '2500-plus') return productPrice >= 250000;
  return false;
};

if (!customElements.get('b1g2-product-row')) {
  customElements.define(
    'b1g2-product-row',
    class B1g2ProductRow extends HTMLElement {
      connectedCallback() {
        if (this.isInitialized) {
          if (this.isCategoryActive) {
            this.applyFilter(this.appliedRole, this.appliedColor, this.appliedPrice, this.appliedSort);
          }
          return;
        }
        this.isInitialized = true;
        this.status = this.querySelector('.b1g2-group__status');
        this.categoryKey = this.dataset.categoryKey;
        this.categoryLabel = this.dataset.categoryLabel;
        this.groupSection = this.closest('.b1g2-group-section');
        this.group = this.closest('.b1g2-group');
        this.productList = this.querySelector('.b1g2-group__products');
        this.noResults = this.querySelector('[data-b1g2-no-results]');
        this.appliedRole = 'all';
        this.appliedColor = 'all';
        this.appliedPrice = 'all';
        this.appliedSort = 'featured';
        this.initialCount = Math.max(1, Number(this.dataset.initialCount) || B1G2_RENDER_CHUNK_SIZE);
        this.renderVersion = 0;
        this.records = Array.from(
          this.querySelectorAll('.b1g2-group__product, template[data-b1g2-product-template]')
        ).map((node, index) => ({
          element: node.matches('.b1g2-group__product') ? node : null,
          role: node.dataset.b1g2Role,
          colors: this.parseColors(node.dataset.b1g2Colors),
          price: Number(node.dataset.b1g2Price),
          title: node.dataset.b1g2Title || '',
          productId: node.dataset.b1g2ProductId || '',
          productHandle: node.dataset.b1g2ProductHandle || '',
          originalIndex: index,
          template: node.matches('template') ? node : null,
        }));
        this.setupDeferredRendering();
      }

      disconnectedCallback() {
        this.cancelPendingRender();
        this.renderObserver?.disconnect();
      }

      setupDeferredRendering() {
        if (!this.productList || !this.records.some((record) => record.template)) return;

        this.loadSentinel = document.createElement('li');
        this.loadSentinel.className = 'b1g2-group__load-sentinel';
        this.loadSentinel.setAttribute('aria-hidden', 'true');
        this.productList.append(this.loadSentinel);

        if (!('IntersectionObserver' in window)) return;

        this.renderObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.requestDeferredChunk();
        }, { rootMargin: B1G2_RENDER_ROOT_MARGIN });
        this.isObservingDeferred = false;
      }

      parseColors(value) {
        if (!value) return [];

        try {
          const parsed = JSON.parse(value);
          const colors = Array.isArray(parsed) ? parsed : [parsed];
          return colors
            .map((color) => (typeof color === 'string' ? color : color?.name))
            .filter((color) => typeof color === 'string')
            .map((color) => color.trim())
            .filter(Boolean);
        } catch (error) {
          return [];
        }
      }

      get items() {
        return this.records.map((record) => record.element || record.template);
      }

      get matchingRecords() {
        return this.records.filter((record) => {
          const matchesRole = matchesB1g2Offer(this.appliedRole, record.role);
          const matchesColor =
            this.appliedColor === 'all' ||
            record.colors.some((color) => color.toLowerCase() === this.appliedColor);
          const matchesPrice = matchesB1g2Price(this.appliedPrice, record.price);
          return matchesRole && matchesColor && matchesPrice;
        });
      }

      get sortedMatchingRecords() {
        const records = [...this.matchingRecords];

        if (this.appliedSort === 'price-ascending') {
          return records.sort((a, b) => a.price - b.price || a.originalIndex - b.originalIndex);
        }
        if (this.appliedSort === 'price-descending') {
          return records.sort((a, b) => b.price - a.price || a.originalIndex - b.originalIndex);
        }
        if (this.appliedSort === 'title-ascending') {
          return records.sort((a, b) => a.title.localeCompare(b.title) || a.originalIndex - b.originalIndex);
        }

        return records.sort((a, b) => a.originalIndex - b.originalIndex);
      }

      get shownItems() {
        return this.matchingRecords
          .map((record) => record.element)
          .filter((item) => item && !item.hidden);
      }

      materialize(record) {
        if (record.element) return record.element;

        const item = record.template?.content?.firstElementChild?.cloneNode(true);
        if (!item) return null;

        record.template.remove();
        record.element = item;
        record.template = null;
        return item;
      }

      applyFilter(role, color = 'all', price = 'all', sort = 'featured') {
        this.cancelPendingRender();
        this.appliedRole = role;
        this.appliedColor = color;
        this.appliedPrice = price;
        this.appliedSort = sort;
        const matchingRecords = this.sortedMatchingRecords;
        const matchingSet = new Set(matchingRecords);
        const renderVersion = this.renderVersion;

        this.records.forEach((record) => {
          if (!record.element) return;
          const isMatch = matchingSet.has(record);
          record.element.classList.toggle('is-filtered-out', !isMatch);
          if (record.element.hidden === isMatch) record.element.hidden = !isMatch;
        });

        this.syncItemOrder(matchingRecords);

        const deferredRecords = matchingRecords.filter((record) => !record.element);
        this.deferredRecords = deferredRecords;
        this.pendingMatchingRecords = matchingRecords;
        this.pendingRenderVersion = renderVersion;
        if (deferredRecords.length) {
          const initialDeferredCount = matchingRecords
            .slice(0, this.initialCount)
            .filter((record) => !record.element).length;

          if (initialDeferredCount) {
            this.setAttribute('aria-busy', 'true');
            this.renderFrame = window.requestAnimationFrame(() => {
              this.renderFrame = window.requestAnimationFrame(() => {
                this.renderFrame = null;
                this.materializeChunk(renderVersion, initialDeferredCount);
              });
            });
          } else if (this.renderObserver) {
            this.observeDeferredRecords();
          } else {
            this.requestDeferredChunk();
          }
        } else {
          this.removeAttribute('aria-busy');
        }

        if (this.noResults) this.noResults.hidden = matchingRecords.length > 0;
        this.updateControls(matchingRecords.length);
        return matchingRecords.length;
      }

      requestDeferredChunk() {
        if (
          this.renderFrame ||
          !this.deferredRecords?.length ||
          this.pendingRenderVersion !== this.renderVersion
        ) return;

        this.setAttribute('aria-busy', 'true');
        const renderVersion = this.pendingRenderVersion;
        this.renderFrame = window.requestAnimationFrame(() => {
          this.renderFrame = null;
          this.materializeChunk(renderVersion);
        });
      }

      observeDeferredRecords() {
        if (!this.renderObserver || !this.loadSentinel || !this.deferredRecords?.length) return;

        this.renderObserver.unobserve(this.loadSentinel);
        this.renderObserver.observe(this.loadSentinel);
        this.isObservingDeferred = true;
      }

      materializeChunk(renderVersion, chunkSize = B1G2_RENDER_CHUNK_SIZE) {
        if (renderVersion !== this.renderVersion || !this.productList) return;

        const renderStarted = performance.now();
        const materializedItems = this.deferredRecords.splice(0, chunkSize).map((record) => {
          const item = this.materialize(record);
          if (!item) return null;

          item.classList.remove('is-filtered-out');
          item.hidden = false;
          return item;
        }).filter(Boolean);
        this.insertMaterializedItems(this.pendingMatchingRecords, materializedItems);
        this.removeAttribute('aria-busy');

        if (this.deferredRecords.length) {
          if (this.renderObserver) {
            if (!this.isObservingDeferred) this.observeDeferredRecords();
          } else {
            this.requestDeferredChunk();
          }
        } else if (this.renderObserver && this.loadSentinel) {
          this.renderObserver.unobserve(this.loadSentinel);
          this.isObservingDeferred = false;
        }

        const renderDuration = roundB1g2Timing(performance.now() - renderStarted);
        window.setTimeout(() => trackB1g2Performance('render_chunk_perf', {
          b1g2_perf_source: 'deferred_chunk',
          b1g2_total_ms: renderDuration,
          b1g2_processing_ms: renderDuration,
          b1g2_rendered_count: materializedItems.length,
          b1g2_remaining_count: this.deferredRecords.length,
          b1g2_slowest_phase: 'filter_dom',
          b1g2_long_task_count: renderDuration >= 50 ? 1 : 0,
        }), 0);
      }

      insertMaterializedItems(matchingRecords, materializedItems) {
        if (!this.productList || !materializedItems.length) return;

        const pendingItems = new Set(materializedItems);
        let anchor = this.loadSentinel || null;
        for (let index = matchingRecords.length - 1; index >= 0; index -= 1) {
          const item = matchingRecords[index].element;
          if (!item) continue;
          if (pendingItems.has(item)) this.productList.insertBefore(item, anchor);
          anchor = item;
        }
      }

      syncItemOrder(matchingRecords) {
        if (!this.productList) return;

        const matchingItems = matchingRecords
          .map((record) => record.element)
          .filter(Boolean);
        const matchingItemsSet = new Set(matchingItems);
        const currentOrder = Array.from(this.productList.children)
          .filter((item) => matchingItemsSet.has(item));
        const orderChanged =
          currentOrder.length !== matchingItems.length ||
          matchingItems.some((item, index) => currentOrder[index] !== item);

        if (!orderChanged) return;

        const fragment = document.createDocumentFragment();
        matchingItems.forEach((item) => fragment.append(item));
        this.productList.insertBefore(fragment, this.loadSentinel || null);
      }

      cancelPendingRender() {
        this.renderVersion += 1;
        if (this.renderFrame) window.cancelAnimationFrame(this.renderFrame);
        this.renderObserver?.unobserve(this.loadSentinel);
        this.isObservingDeferred = false;
        this.renderFrame = null;
        this.deferredRecords = [];
        this.pendingMatchingRecords = [];
        this.pendingRenderVersion = this.renderVersion;
        this.removeAttribute('aria-busy');
      }

      setActive(active) {
        this.isCategoryActive = active;
        if (!active) this.cancelPendingRender();
        if (this.group) this.group.hidden = !active;
        if (!this.groupSection) return;

        this.groupSection.hidden = !active;
        this.groupSection.classList.toggle('is-active-category', active);
        this.groupSection.setAttribute('aria-hidden', String(!active));
      }

      updateControls(count = this.shownItems.length) {
        if (this.status) {
          this.status.textContent = `Showing ${count} products`;
        }
      }
    }
  );
}

if (!customElements.get('b1g2-role-filter')) {
  customElements.define(
    'b1g2-role-filter',
    class B1g2RoleFilter extends HTMLElement {
      connectedCallback() {
        this.pageRoot = this.closest('main') || document.querySelector('main') || document;
        this.form = this.querySelector('[data-b1g2-filter-form]');
        this.status = this.querySelector('[data-b1g2-filter-status]');
        this.productCount = this.querySelector('[data-b1g2-product-count]');
        this.colorPanel = this.querySelector('[data-b1g2-color-panel]');
        this.colorOptions = this.querySelector('[data-b1g2-color-options]');
        this.sortSelect = this.querySelector('[data-b1g2-sort]');
        this.categoryNav = this.querySelector('[data-b1g2-category-nav]');
        this.categoryTabs = this.querySelector('[data-b1g2-category-tabs]');
        this.countTemplate = this.dataset.countTemplate;
        this.statusTemplate = this.dataset.statusTemplate;

        this.populateColorOptions();
        this.radios = Array.from(this.querySelectorAll(
          'input[name="b1g2_offer"], input[name="b1g2_color"], input[name="b1g2_price"]'
        ));
        if (!this.form || !this.radios.length) return;

        this.buildCategoryTabs();
        const restoredState = this.readUrlState();
        this.activeCategory = this.rows.some((row) => row.categoryKey === restoredState.category)
          ? restoredState.category
          : this.rows[0]?.categoryKey;
        this.restoreRadio('b1g2_offer', restoredState.offer);
        this.restoreRadio('b1g2_color', restoredState.color);
        this.restoreRadio('b1g2_price', restoredState.price);
        const restoredSortExists = Array.from(this.sortSelect?.options || [])
          .some((option) => option.value === restoredState.sort);
        if (restoredSortExists) {
          this.sortSelect.value = restoredState.sort;
        }
        this.refreshCounts();
        this.updateOptionState();
        this.applyFilters({ syncUrl: false });
        this.syncUrlState();

        this.onFormSubmit = (event) => event.preventDefault();
        this.onFormChange = (event) => {
          if (!event.target.matches(
            'input[name="b1g2_offer"], input[name="b1g2_color"], input[name="b1g2_price"], select[name="b1g2_sort"]'
          )) return;

          const source = event.target.name === 'b1g2_sort'
            ? 'sort'
            : event.target.name.replace('b1g2_', '');
          const finishMeasurement = beginB1g2Interaction(event, source);
          const optionStarted = performance.now();
          this.updateOptionState();
          const optionDuration = roundB1g2Timing(performance.now() - optionStarted);
          const countsStarted = performance.now();
          this.refreshCounts();
          const countsDuration = roundB1g2Timing(performance.now() - countsStarted);
          const filterTimings = this.applyFilters({ track: true, source });
          finishMeasurement({
            ...filterTimings,
            b1g2_option_ms: optionDuration,
            b1g2_counts_ms: countsDuration,
          });
        };
        this.form.addEventListener('submit', this.onFormSubmit);
        this.form.addEventListener('change', this.onFormChange);

        this.clearFilterButtons = Array.from(this.pageRoot.querySelectorAll('[data-b1g2-clear-filters]'));
        this.onClearFilters = (event) => {
          const finishMeasurement = beginB1g2Interaction(event, 'clear');
          const timings = this.clearFilters();
          finishMeasurement(timings);
        };
        this.clearFilterButtons.forEach((button) => button.addEventListener('click', this.onClearFilters));

        this.onFilterClick = (event) => {
          const categoryTab = event.target.closest('[data-b1g2-category-tab]');
          if (categoryTab) {
            const finishMeasurement = beginB1g2Interaction(event, 'category');
            const timings = this.selectCategory(categoryTab.dataset.b1g2CategoryTab);
            if (timings) finishMeasurement(timings);
          }
        };
        this.addEventListener('click', this.onFilterClick);

        this.onKeydown = (event) => {
          const currentTab = event.target.closest?.('[data-b1g2-category-tab]');
          if (!currentTab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

          const tabs = Array.from(this.querySelectorAll('[data-b1g2-category-tab]'));
          const currentIndex = tabs.indexOf(currentTab);
          const nextIndex =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? tabs.length - 1
                : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          event.preventDefault();
          tabs[nextIndex]?.focus();
        };
        this.addEventListener('keydown', this.onKeydown);

        this.onPageClick = (event) => this.trackPageClick(event);
        document.addEventListener('click', this.onPageClick);

        if (!window.__b1g2PageViewTracked) {
          window.__b1g2PageViewTracked = true;
          trackB1g2Event('page_view', {
            b1g2_category: this.activeCategory || 'none',
          });
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onFormSubmit);
        this.form?.removeEventListener('change', this.onFormChange);
        this.removeEventListener('click', this.onFilterClick);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onPageClick);
        this.clearFilterButtons?.forEach((button) => button.removeEventListener('click', this.onClearFilters));
      }

      get checkedOfferRadio() {
        return this.radios.find((radio) => radio.name === 'b1g2_offer' && radio.checked);
      }

      get checkedColorRadio() {
        return this.radios.find((radio) => radio.name === 'b1g2_color' && radio.checked);
      }

      get checkedPriceRadio() {
        return this.radios.find((radio) => radio.name === 'b1g2_price' && radio.checked);
      }

      get rows() {
        return Array.from(this.pageRoot.querySelectorAll('b1g2-product-row'));
      }

      get activeRow() {
        return this.rows.find((row) => row.categoryKey === this.activeCategory);
      }

      readUrlState() {
        const params = new URLSearchParams(window.location.search);
        return {
          category: params.get('b1g2_category'),
          offer: params.get('b1g2_offer') || 'all',
          color: params.get('b1g2_color') || 'all',
          price: params.get('b1g2_price') || 'all',
          sort: params.get('b1g2_sort') || 'featured',
        };
      }

      restoreRadio(name, value) {
        const matchingRadio = this.radios.find((radio) => radio.name === name && radio.value === value);
        if (matchingRadio) matchingRadio.checked = true;
      }

      buildCategoryTabs() {
        if (!this.categoryNav || !this.categoryTabs || !this.rows.length) return;

        const fragment = document.createDocumentFragment();
        this.rows.forEach((row, index) => {
          if (row.groupSection) row.groupSection.id = `b1g2-category-${row.categoryKey || index}`;

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'b1g2-category-nav__tab';
          button.dataset.b1g2CategoryTab = row.categoryKey;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', row.groupSection?.id || '');

          const label = document.createElement('span');
          label.textContent = row.categoryLabel;

          button.append(label);
          fragment.append(button);
        });

        this.categoryTabs.replaceChildren(fragment);
        this.categoryNav.hidden = false;
      }

      syncCategoryTabs() {
        this.rows.forEach((row) => row.setActive(row.categoryKey === this.activeCategory));
        this.querySelectorAll('[data-b1g2-category-tab]').forEach((tab) => {
          const active = tab.dataset.b1g2CategoryTab === this.activeCategory;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
      }

      selectCategory(category) {
        if (!this.rows.some((row) => row.categoryKey === category)) return;
        if (this.activeCategory === category) return;

        const scrollPosition = { left: window.scrollX, top: window.scrollY };
        this.activeCategory = category;
        const countsStarted = performance.now();
        this.refreshCounts();
        const countsDuration = roundB1g2Timing(performance.now() - countsStarted);
        const filterTimings = this.applyFilters({ track: true, source: 'category' });
        window.scrollTo({ ...scrollPosition, behavior: 'instant' });
        return { ...filterTimings, b1g2_counts_ms: countsDuration };
      }

      syncUrlState() {
        if (!this.activeCategory) return;

        const url = new URL(window.location.href);
        url.searchParams.set('b1g2_category', this.activeCategory);

        if (this.appliedRole && this.appliedRole !== 'all') {
          url.searchParams.set('b1g2_offer', this.appliedRole);
        } else {
          url.searchParams.delete('b1g2_offer');
        }

        if (this.appliedColor && this.appliedColor !== 'all') {
          url.searchParams.set('b1g2_color', this.appliedColor);
        } else {
          url.searchParams.delete('b1g2_color');
        }

        if (this.appliedPrice && this.appliedPrice !== 'all') {
          url.searchParams.set('b1g2_price', this.appliedPrice);
        } else {
          url.searchParams.delete('b1g2_price');
        }

        if (this.appliedSort && this.appliedSort !== 'featured') {
          url.searchParams.set('b1g2_sort', this.appliedSort);
        } else {
          url.searchParams.delete('b1g2_sort');
        }

        url.searchParams.delete('b1g2_shown');

        window.history.replaceState({ ...window.history.state, b1g2: true }, '', url);
      }

      populateColorOptions() {
        if (!this.colorPanel || !this.colorOptions) return;

        this.colorOptions.querySelectorAll('[data-b1g2-generated-color]').forEach((option) => option.remove());

        const labels = new Map();
        this.rows.forEach((row) => {
          row.records.forEach((record) => {
            record.colors.forEach((label) => labels.set(label.toLowerCase(), label));
          });
        });

        if (!labels.size) return;

        const preferredOrder = [
          'cloud dancer white',
          'blush',
          'sage green',
          'berry & burgundy',
          'terri',
          'french blue & purple',
          'mix',
        ];
        const orderIndex = new Map(preferredOrder.map((label, index) => [label, index]));
        const entries = Array.from(labels.entries()).sort(([keyA, labelA], [keyB, labelB]) => {
          const indexA = orderIndex.has(keyA) ? orderIndex.get(keyA) : preferredOrder.length;
          const indexB = orderIndex.has(keyB) ? orderIndex.get(keyB) : preferredOrder.length;
          return indexA - indexB || labelA.localeCompare(labelB);
        });

        entries.forEach(([key, label]) => {
          const option = document.createElement('label');
          option.className = 'rm-opt';
          option.dataset.b1g2GeneratedColor = '';

          const input = document.createElement('input');
          input.type = 'radio';
          input.name = 'b1g2_color';
          input.value = key;

          const name = document.createElement('span');
          name.className = 'rm-opt__name';
          name.textContent = label;

          const count = document.createElement('span');
          count.className = 'rm-opt__n';
          count.dataset.b1g2ColorCount = key;

          option.append(input, name, count);
          this.colorOptions.append(option);
        });

        this.colorPanel.hidden = false;
      }

      refreshCounts() {
        const counts = { b1: 0, g2: 0, b1g2: 0 };
        const colorCounts = new Map();
        const priceCounts = new Map();
        let total = 0;
        const offer = this.checkedOfferRadio?.value || 'all';
        const color = this.checkedColorRadio?.value || 'all';
        const price = this.checkedPriceRadio?.value || 'all';

        const rowsToCount = this.activeRow ? [this.activeRow] : this.rows;
        rowsToCount.forEach((row) => {
          row.records.forEach((record) => {
            total += 1;
            Object.keys(counts).forEach((role) => {
              const matchesColor =
                color === 'all' || record.colors.some((label) => label.toLowerCase() === color);
              if (matchesColor && matchesB1g2Price(price, record.price) && matchesB1g2Offer(role, record.role)) {
                counts[role] += 1;
              }
            });
            record.colors.forEach((label) => {
              const key = label.toLowerCase();
              if (matchesB1g2Offer(offer, record.role) && matchesB1g2Price(price, record.price)) {
                colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
              }
            });
            ['under-1000', '1000-1999', '2000-2499', '2500-plus'].forEach((range) => {
              const matchesColor =
                color === 'all' || record.colors.some((label) => label.toLowerCase() === color);
              if (matchesB1g2Offer(offer, record.role) && matchesColor && matchesB1g2Price(range, record.price)) {
                priceCounts.set(range, (priceCounts.get(range) || 0) + 1);
              }
            });
          });
        });

        Object.entries(counts).forEach(([role, count]) => {
          const target = this.querySelector(`[data-b1g2-role-count="${role}"]`);
          if (target) this.updateFacetCount(target, count);
        });
        this.querySelectorAll('[data-b1g2-color-count]').forEach((target) => {
          this.updateFacetCount(target, colorCounts.get(target.dataset.b1g2ColorCount) || 0);
        });
        this.querySelectorAll('[data-b1g2-price-count]').forEach((target) => {
          this.updateFacetCount(target, priceCounts.get(target.dataset.b1g2PriceCount) || 0);
        });

        this.total = total;
        this.updateProductCount(total);
      }

      updateFacetCount(target, count) {
        target.textContent = count;
        const option = target.closest('.rm-opt');
        const input = option?.querySelector('input');
        const isEmpty = count === 0 && input?.checked === false;
        option?.classList.toggle('is-empty', isEmpty);
        if (input) input.disabled = isEmpty;
      }

      updateOptionState() {
        this.radios.forEach((radio) => {
          radio.closest('.rm-opt')?.classList.toggle('is-on', radio.checked);
        });
      }

      updateProductCount(count) {
        if (!this.productCount || !this.countTemplate) return;
        this.productCount.textContent = this.countTemplate.replace('[count]', count);
      }

      applyFilters({ syncUrl = true, track = false, source = 'filter' } = {}) {
        const selectedOffer = this.checkedOfferRadio;
        const selectedColor = this.checkedColorRadio;
        const selectedPrice = this.checkedPriceRadio;
        const role = selectedOffer?.value || 'all';
        const color = selectedColor?.value || 'all';
        const price = selectedPrice?.value || 'all';
        const sort = this.sortSelect?.value || 'featured';
        const activeRow = this.activeRow;
        const filterDomStarted = performance.now();
        const matchCount = activeRow ? activeRow.applyFilter(role, color, price, sort) : 0;
        const filterDomDuration = roundB1g2Timing(performance.now() - filterDomStarted);
        const selectedLabels = [selectedOffer, selectedPrice, selectedColor]
          .filter((radio) => radio && radio.value !== 'all')
          .map((radio) => radio.closest('.rm-opt')?.querySelector('.rm-opt__name')?.textContent.trim())
          .filter(Boolean);

        this.appliedRole = role;
        this.appliedColor = color;
        this.appliedPrice = price;
        this.appliedSort = sort;
        this.updateProductCount(matchCount);
        const tabsStarted = performance.now();
        this.syncCategoryTabs();
        const tabsDuration = roundB1g2Timing(performance.now() - tabsStarted);

        if (this.status && this.statusTemplate) {
          this.status.textContent = this.statusTemplate
            .replace('[count]', matchCount)
            .replace(
              '[filter]',
              [activeRow?.categoryLabel, ...selectedLabels].filter(Boolean).join(' · ') || 'All'
            );
          this.status.hidden = false;
        }

        const urlStarted = performance.now();
        if (syncUrl) this.syncUrlState();
        const urlDuration = roundB1g2Timing(performance.now() - urlStarted);

        const trackingStarted = performance.now();
        if (track) {
          trackB1g2Event('filter_select', {
            b1g2_filter_source: source,
            b1g2_category: this.activeCategory || 'none',
            b1g2_offer: role,
            b1g2_color: color,
            b1g2_price: price,
            b1g2_sort: sort,
            b1g2_match_count: matchCount,
          });
        }
        const trackingDuration = roundB1g2Timing(performance.now() - trackingStarted);
        return {
          b1g2_match_count: matchCount,
          b1g2_filter_dom_ms: filterDomDuration,
          b1g2_tabs_ms: tabsDuration,
          b1g2_url_ms: urlDuration,
          b1g2_tracking_ms: trackingDuration,
        };
      }

      trackPageClick(event) {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        if (!target) return;

        const checkoutControl = target.closest('a[href*="/checkout"], button[name="checkout"], [data-checkout]');
        if (checkoutControl) {
          trackB1g2Event('page_checkout_click', {
            b1g2_attribution: 'page_context_only',
          });
          return;
        }

        const product = target.closest('.b1g2-group__product');
        const row = product?.closest('b1g2-product-row');
        if (!product || !row) return;

        const productDetails = {
          b1g2_category: row.categoryKey || 'none',
          b1g2_product_id: product.dataset.b1g2ProductId || '',
          b1g2_product_handle: product.dataset.b1g2ProductHandle || '',
          b1g2_offer: product.dataset.b1g2Role || 'none',
        };
        const addControl = target.closest(
          'add-to-cart, button[name="add"], form[action*="/cart/add"] button[type="submit"], [data-add-to-cart]'
        );

        if (addControl && product.contains(addControl)) {
          trackB1g2Event('add_to_cart_click', productDetails);
          return;
        }

        const productLink = target.closest('a[href*="/products/"]');
        if (productLink && product.contains(productLink)) {
          trackB1g2Event('product_click', productDetails);
        }
      }

      clearFilters() {
        ['b1g2_offer', 'b1g2_color', 'b1g2_price'].forEach((name) => {
          const allRadio = this.radios.find((radio) => radio.name === name && radio.value === 'all');
          if (allRadio) allRadio.checked = true;
        });
        const optionStarted = performance.now();
        this.updateOptionState();
        const optionDuration = roundB1g2Timing(performance.now() - optionStarted);
        const countsStarted = performance.now();
        this.refreshCounts();
        const countsDuration = roundB1g2Timing(performance.now() - countsStarted);
        return {
          ...this.applyFilters({ track: true, source: 'clear' }),
          b1g2_option_ms: optionDuration,
          b1g2_counts_ms: countsDuration,
        };
      }

    }
  );
}
